<template>
  <BaseModal
    :visible="true"
    :title="modalTitle"
    size="xl"
    footer-align="start"
    elevated-footer
    @close="handleCancel"
  >
    <base-loader v-if="dataIsLoading" />

    <form
      v-else
      :id="formId"
      class="driver-form"
      @submit.prevent="saveDriver"
    >
      <div ref="formBodyRef">
        <driver-create-form
          class="driver-block form-layout--compact"
          :form-data="formData"
          :form-id="formId"
          :errors="errors"
          @update:form-data="updateFormData"
          @clear-error="clearError"
          ref="formRef"
        />

        <requisite-create
          v-if="createMode"
          ref="requisiteCreateRef"
          :allowed-models="['Driver']"
          :requisitable-type="'Driver'"
          :form-id="formId"
        />
      </div>
    </form>

    <template #footer>
      <base-primary-button
        type="button"
        :form="formId"
        :disabled="isSubmitting || dataIsLoading"
        @click="saveDriver"
      >
        <span v-if="isSubmitting">Сохранение...</span>
        <span v-else>Сохранить</span>
      </base-primary-button>

      <base-button
        type="button"
        @click="handleCancel"
        :disabled="isSubmitting"
      >
        Отмена
      </base-button>
    </template>
  </BaseModal>
</template>

<script>
import { getCompanyId } from "@/utils/userUtils";
import {
  buildDriverLicensePayload,
  createEmptyDriverLicense,
} from "@/utils/driverLicenseUtils";
import {
  buildDriverPassportPayload,
  createEmptyDriverPassport,
} from "@/utils/driverPassportUtils";
import { useNotificationStore } from "@/stores/notificationStore";
import { useDriverStore } from "@/stores/driverStore";

import driversApi from "@/services/api/driversApi";

import DriverCreateForm from "@/components/Driver/DriverCreateForm.vue";
import RequisiteCreate from "@/components/Requisite/RequisiteCreate.vue";

export default {
  name: "AddDriverModal",
  components: {
    DriverCreateForm,
    RequisiteCreate,
  },
  props: {
    driverId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["close", "driver-created", "driver-updated"],
  data() {
    return {
      dataIsLoading: Boolean(this.driverId),
      isSubmitting: false,
      formId: "driver-modal-form",

      companyId: getCompanyId(),

      formData: {
        last_name: "",
        first_name: "",
        middle_name: "",
        inn: "",
        phone: "",
        employment_date: "",
        driver_license: createEmptyDriverLicense(),
        driver_passport: createEmptyDriverPassport(),
      },

      errors: {
        last_name: false,
        first_name: false,
      },
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },
    driverStore() {
      return useDriverStore();
    },

    createMode() {
      return !this.driverId;
    },

    modalTitle() {
      return this.createMode
        ? "Создание нового водителя"
        : "Редактирование водителя";
    },
  },

  methods: {
    updateFormData(newData) {
      Object.assign(this.formData, newData);
    },

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false;
      }
    },

    async saveDriver() {
      const formComponent = this.$refs.formRef;
      const isFormValid =
        this.validateForm() &&
        (!formComponent ||
          typeof formComponent.validate !== "function" ||
          (await formComponent.validate()));

      if (!isFormValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );

        this.$nextTick(() => {
          const firstErrorElement =
            this.$refs.formBodyRef?.querySelector(
              ".invalid, .base-form-field__error"
            );

          if (firstErrorElement) {
            const formGroup =
              firstErrorElement.closest(".form-group");
            const targetElement = formGroup || firstErrorElement;
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });

        return;
      }

      try {
        this.isSubmitting = true;
        const submittedFormData =
          typeof formComponent?.currentFormData === "function"
            ? formComponent.currentFormData()
            : this.formData;

        this.updateFormData(submittedFormData);

        if (this.createMode) {
          await this.createDriver(submittedFormData);
        } else {
          await this.updateDriver(submittedFormData);
        }
      } catch (error) {
        console.error("Ошибка при сохранении водителя:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    validateForm() {
      let isValid = true;

      if (!String(this.formData.last_name || "").trim()) {
        this.errors.last_name = true;
        isValid = false;
      }
      if (!String(this.formData.first_name || "").trim()) {
        this.errors.first_name = true;
        isValid = false;
      }

      return isValid;
    },

    driverPayload(formData) {
      const payload = {
        phone: formData.phone,
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name || null,
        inn: formData.inn || null,
        employment_date: formData.employment_date || null,
      };
      const driverLicensePayload = buildDriverLicensePayload(formData);
      if (driverLicensePayload) {
        payload.driver_license = driverLicensePayload;
      }
      const driverPassportPayload = buildDriverPassportPayload(formData);
      if (driverPassportPayload) {
        payload.driver_passport = driverPassportPayload;
      }

      return payload;
    },

    createFormData(driver = {}) {
      this.formData = {
        last_name: driver.last_name || "",
        first_name: driver.first_name || "",
        middle_name: driver.middle_name || "",
        inn: driver.inn || "",
        phone: driver.phone || "",
        employment_date: this.dateValue(driver.employment_date),
        driver_license: driver.driver_license || createEmptyDriverLicense(),
        driver_passport: driver.driver_passport || createEmptyDriverPassport(),
      };
    },

    dateValue(value) {
      if (!value) return "";

      return String(value).slice(0, 10);
    },

    async createDriver(formData) {
      const response = await driversApi.create(
        this.companyId,
        this.driverPayload(formData)
      );

      const newDriver = response.data.result;
      const driverId = newDriver.id;

      await this.$refs.requisiteCreateRef.createRequisites(driverId);

      if (this.driverStore.drivers) {
        await this.driverStore.fetchDrivers();
      }

      this.notificationStore.showSuccess(
        "Новый водитель успешно создан"
      );

      this.$emit("driver-created", newDriver);
      this.$emit("close");
    },

    async updateDriver(formData) {
      const response = await driversApi.update(
        this.companyId,
        this.driverId,
        this.driverPayload(formData)
      );

      const driver = response.data.result;
      this.driverStore.replaceDriver(driver);

      this.notificationStore.showSuccess("Водитель успешно обновлен");

      this.$emit("driver-updated", driver);
      this.$emit("close");
    },

    async fetchDriver() {
      if (this.createMode) return;

      this.dataIsLoading = true;

      try {
        const response = await driversApi.show(
          this.companyId,
          this.driverId
        );

        this.createFormData(response.data.result);
      } finally {
        this.dataIsLoading = false;
      }
    },

    handleCancel() {
      this.$emit("close");
    },
  },

  mounted() {
    this.fetchDriver();
  },
};
</script>

<style scoped>
.driver-form {
  display: flex;
  flex-direction: column;
}

.driver-block {
  margin-bottom: var(--base-out-indent);
}
</style>
